/**
 * Task Path Converter
 *
 * Bidirectional conversion between tree node keys and task paths
 * Format: @task[ID].field[index]
 */

import type { TaskTreeNode } from './types';
import {
  findNodeByKey,
  collectAllCheckableKeys,
  isFullyChecked,
  markAsProcessed,
} from '../../shared/treeHelpers';

/**
 * Converts tree node key to path
 *
 * @param nodeKey - Node key from tree
 * @param allNodes - All tree nodes
 * @returns Path string or null
 */
export function treeNodeIdToPath(nodeKey: string, allNodes: TaskTreeNode[]): string | null {
  const node = findNodeByKey(nodeKey, allNodes) as TaskTreeNode | null;
  if (!node) return null;

  switch (node.nodeType) {
    case 'task':
      // Whole task: @task[ID]
      return `@task[${node.taskId}]`;

    case 'folder':
      // Whole array field: @task[ID].funnelGoals
      return `@task[${node.taskId}].${node.fieldName}`;

    case 'field':
      // Single field: @task[ID].name
      return `@task[${node.taskId}].${node.fieldName}`;

    case 'arrayItem':
      // Array item: @task[ID].funnelGoals[0]
      return `@task[${node.taskId}].${node.fieldName}[${node.arrayIndex}]`;

    default:
      return null;
  }
}

/**
 * Converts checked tree node IDs to optimized paths
 *
 * @param checkedIds - Array of checked node keys
 * @param allNodes - All tree nodes
 * @returns Optimized array of paths
 */
export function treeNodeIdsToPaths(checkedIds: string[], allNodes: TaskTreeNode[]): string[] {
  const checkedSet = new Set(checkedIds);
  const processedNodes = new Set<string>();
  const paths: string[] = [];

  // Process each task node
  for (const taskNode of allNodes) {
    if (processedNodes.has(taskNode.key as string)) continue;

    // Check if whole task is selected
    if (isFullyChecked(taskNode, checkedSet)) {
      const path = treeNodeIdToPath(taskNode.key as string, allNodes);
      if (path) {
        paths.push(path);
        markAsProcessed(taskNode, processedNodes);
      }
      continue;
    }

    // Check folder nodes (array fields)
    if (taskNode.children) {
      for (const child of taskNode.children as TaskTreeNode[]) {
        if (child.nodeType === 'folder') {
          if (processedNodes.has(child.key as string)) continue;

          if (isFullyChecked(child, checkedSet)) {
            const path = treeNodeIdToPath(child.key as string, allNodes);
            if (path) {
              paths.push(path);
              markAsProcessed(child, processedNodes);
            }
          }
        }
      }
    }
  }

  // Add remaining individual paths (not optimized)
  for (const id of checkedIds) {
    if (!processedNodes.has(id)) {
      const path = treeNodeIdToPath(id, allNodes);
      if (path) {
        paths.push(path);
      }
    }
  }

  return paths;
}

/**
 * Parses task path
 *
 * @param path - Path string
 * @returns Parsed components or null
 */
function parsePath(path: string): {
  taskId?: string;
  fieldName?: string;
  arrayIndex?: number;
  type: 'task' | 'field' | 'arrayItem' | 'folder';
} | null {
  // Whole task: @task[ID]
  const taskMatch = path.match(/^@task\[([^\]]+)\]$/);
  if (taskMatch) {
    return { taskId: taskMatch[1], type: 'task' };
  }

  // Array item: @task[ID].field[index] with optional .suffix (e.g. funnelGoals[0].goal)
  const arrayItemMatch = path.match(/^@task\[([^\]]+)\]\.([^[]+)\[(\d+)\](?:\.\w+)?$/);
  if (arrayItemMatch) {
    return {
      taskId: arrayItemMatch[1],
      fieldName: arrayItemMatch[2],
      arrayIndex: parseInt(arrayItemMatch[3], 10),
      type: 'arrayItem',
    };
  }

  // Field or folder: @task[ID].field (also strip .suffix for competitorsForClients etc.)
  const fieldMatch = path.match(/^@task\[([^\]]+)\]\.([^[.]+)$/);
  if (fieldMatch) {
    const fieldName = fieldMatch[2];
    const arrayFieldNames = ['funnelGoals', 'requiredInfo', 'competitorsForClients', 'userCondition'];

    return {
      taskId: fieldMatch[1],
      fieldName,
      type: arrayFieldNames.includes(fieldName) ? 'folder' : 'field',
    };
  }

  return null;
}

/**
 * Converts single path to tree node IDs (for hydration)
 *
 * @param path - Path string
 * @param allNodes - All tree nodes
 * @returns Array of node keys
 */
function pathToTreeNodeIds_single(path: string, allNodes: TaskTreeNode[]): string[] {
  const parsed = parsePath(path);
  if (!parsed) return [];

  // Whole task - return all checkable keys
  if (parsed.type === 'task') {
    const taskNode = findNodeByKey(`task-${parsed.taskId}`, allNodes) as TaskTreeNode | null;
    if (taskNode) {
      return collectAllCheckableKeys(taskNode);
    }
    return [];
  }

  // Whole folder (array field) - return all checkable keys
  if (parsed.type === 'folder') {
    const folderNode = findNodeByKey(
      `task-${parsed.taskId}-${parsed.fieldName}`,
      allNodes
    ) as TaskTreeNode | null;
    if (folderNode) {
      return collectAllCheckableKeys(folderNode);
    }
    return [];
  }

  // Single field
  if (parsed.type === 'field') {
    return [`task-${parsed.taskId}-${parsed.fieldName}`];
  }

  // Array item
  if (parsed.type === 'arrayItem') {
    return [`task-${parsed.taskId}-${parsed.fieldName}-${parsed.arrayIndex}`];
  }

  return [];
}

/**
 * Smart path deduplication - removes child paths ONLY if ALL children are selected
 *
 * Unlike simple prefix deduplication, this function checks via isFullyChecked
 * whether all child nodes are truly selected before optimization.
 *
 * Examples:
 * - All children selected: ["@task[ID]", "@task[ID].name", ...all fields] -> ["@task[ID]"]
 * - Partial selection: ["@task[ID].name"] -> ["@task[ID].name"]
 * - rc-tree first click (incomplete): ["@task[ID]", "@task[ID].name"] -> ["@task[ID].name"]
 *
 * @param paths - Array of paths to deduplicate
 * @param allNodes - All tree nodes (for isFullyChecked verification)
 * @returns Optimized array of paths
 */
export function deduplicatePaths(paths: string[], allNodes: TaskTreeNode[]): string[] {
  if (paths.length === 0) return [];

  const checkedKeys = new Set<string>();

  // Collect all node keys from paths
  for (const path of paths) {
    const nodeKeys = pathToTreeNodeIds_single(path, allNodes);
    nodeKeys.forEach((key) => checkedKeys.add(key));
  }

  // Use same logic as treeNodeIdsToPaths
  const processedNodes = new Set<string>();
  const result: string[] = [];

  // Check whole tasks
  for (const taskNode of allNodes) {
    if (processedNodes.has(taskNode.key as string)) continue;

    if (isFullyChecked(taskNode, checkedKeys)) {
      const path = treeNodeIdToPath(taskNode.key as string, allNodes);
      if (path) {
        result.push(path);
        markAsProcessed(taskNode, processedNodes);
      }
      continue;
    }

    // Check folder nodes (array fields)
    if (taskNode.children) {
      for (const child of taskNode.children as TaskTreeNode[]) {
        if (child.nodeType === 'folder') {
          if (processedNodes.has(child.key as string)) continue;

          if (isFullyChecked(child, checkedKeys)) {
            const path = treeNodeIdToPath(child.key as string, allNodes);
            if (path) {
              result.push(path);
              markAsProcessed(child, processedNodes);
            }
          }
        }
      }
    }
  }

  // Add remaining individual paths (not optimized)
  for (const path of paths) {
    const parsed = parsePath(path);
    if (!parsed) continue;

    let nodeKey: string;
    if (parsed.type === 'task') {
      nodeKey = `task-${parsed.taskId}`;
    } else if (parsed.type === 'folder') {
      nodeKey = `task-${parsed.taskId}-${parsed.fieldName}`;
    } else if (parsed.type === 'field') {
      nodeKey = `task-${parsed.taskId}-${parsed.fieldName}`;
    } else if (parsed.type === 'arrayItem') {
      nodeKey = `task-${parsed.taskId}-${parsed.fieldName}-${parsed.arrayIndex}`;
    } else {
      continue;
    }

    if (!processedNodes.has(nodeKey)) {
      result.push(path);
    }
  }

  return result;
}

/**
 * Converts paths to tree node IDs (for hydration)
 *
 * IMPORTANT: This function is used for HYDRATION (loading saved data into tree state).
 * Deduplication should NOT be applied here - it must only happen in handleCheck
 * when saving data OUT to parent component.
 *
 * @param paths - Array of paths (e.g., ["@task[ID1]", "@task[ID2]"])
 * @param allNodes - All tree nodes
 * @returns Array of node keys (expanded to include all children)
 */
export function pathsToTreeNodeIds(paths: string[], allNodes: TaskTreeNode[]): string[] {
  const keys = new Set<string>();

  for (const path of paths) {
    const nodeKeys = pathToTreeNodeIds_single(path, allNodes);
    nodeKeys.forEach((key) => keys.add(key));
  }

  return Array.from(keys);
}
